import {
  type FormFieldType,
  type FormFieldDef,
  type FormConsent,
} from '@hcc/types';
import {
  Heading,
  Text,
  Code,
  Image,
  Type,
  AlignLeft,
  User,
  Mail,
  Phone,
  Hash,
  Calendar,
  ChevronDownSquare,
  CircleDot,
  CheckSquare,
  ToggleLeft,
  Upload,
  PenLine,
  Table,
  MapPin,
  Paintbrush,
  type LucideIcon,
} from 'lucide-react';

/** Display label, palette group, and icon for each field type. */
export interface FieldTypeMeta {
  type: FormFieldType;
  label: string;
  group: 'Layout' | 'Text' | 'Choice' | 'Advanced';
  icon: LucideIcon;
  hint: string;
}

export const FIELD_TYPE_META: Record<FormFieldType, FieldTypeMeta> = {
  section_heading: { type: 'section_heading', label: 'Section heading', group: 'Layout', icon: Heading, hint: 'A title between groups of fields.' },
  paragraph: { type: 'paragraph', label: 'Paragraph', group: 'Layout', icon: Text, hint: 'Static explanatory text or a notice.' },
  rich_text: { type: 'rich_text', label: 'Rich content (HTML)', group: 'Layout', icon: Code, hint: 'Formatted instructions with links. Paste HTML; links open in a new tab.' },
  image: { type: 'image', label: 'Image', group: 'Layout', icon: Image, hint: 'A static picture, e.g. a size chart.' },
  short_text: { type: 'short_text', label: 'Short text', group: 'Text', icon: Type, hint: 'A single line of text.' },
  long_text: { type: 'long_text', label: 'Long text', group: 'Text', icon: AlignLeft, hint: 'A multi-line text area.' },
  name: { type: 'name', label: 'Name', group: 'Text', icon: User, hint: 'First and last name.' },
  address: { type: 'address', label: 'Address', group: 'Text', icon: MapPin, hint: 'Address block: lines, city, county, post code.' },
  email: { type: 'email', label: 'Email', group: 'Text', icon: Mail, hint: 'A validated email address.' },
  phone: { type: 'phone', label: 'Phone', group: 'Text', icon: Phone, hint: 'A telephone number.' },
  number: { type: 'number', label: 'Number', group: 'Text', icon: Hash, hint: 'A numeric value.' },
  date: { type: 'date', label: 'Date', group: 'Text', icon: Calendar, hint: 'A date picker.' },
  dropdown: { type: 'dropdown', label: 'Dropdown', group: 'Choice', icon: ChevronDownSquare, hint: 'Pick one from a list.' },
  radio: { type: 'radio', label: 'Radio buttons', group: 'Choice', icon: CircleDot, hint: 'Pick one, shown inline.' },
  checkbox: { type: 'checkbox', label: 'Checkboxes', group: 'Choice', icon: CheckSquare, hint: 'Pick one or more.' },
  yes_no: { type: 'yes_no', label: 'Yes / No', group: 'Choice', icon: ToggleLeft, hint: 'A simple yes or no.' },
  file_upload: { type: 'file_upload', label: 'File upload', group: 'Advanced', icon: Upload, hint: 'Attach one or more files.' },
  signature: { type: 'signature', label: 'Signature', group: 'Advanced', icon: PenLine, hint: 'Draw-to-sign pad. Pair with a rich-content block for an agreement.' },
  grid: { type: 'grid', label: 'Grid', group: 'Advanced', icon: Table, hint: 'Rows and columns, e.g. a sizing table.' },
  damage_markup: { type: 'damage_markup', label: 'Damage markup', group: 'Advanced', icon: Paintbrush, hint: 'Draw on a picture, e.g. mark damage on a vehicle diagram. Needs a background image.' },
};

export const PALETTE_GROUPS: FieldTypeMeta['group'][] = [
  'Layout',
  'Text',
  'Choice',
  'Advanced',
];

/** Stable, readable key from a label plus a numeric suffix for uniqueness. */
export function slugifyKey(label: string, suffix: number): string {
  const base = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return (base || 'field') + '_' + suffix;
}

/** Build a sensible default field of the given type. `seq` keeps keys unique. */
export function makeField(type: FormFieldType, seq: number): FormFieldDef {
  const meta = FIELD_TYPE_META[type];
  const field: FormFieldDef = {
    key: slugifyKey(meta.label, seq),
    type,
    label: meta.label,
  };
  if (type === 'dropdown' || type === 'radio' || type === 'checkbox') {
    field.options = [
      { value: 'option_1', label: 'Option 1' },
      { value: 'option_2', label: 'Option 2' },
    ];
  }
  if (type === 'grid') {
    field.grid = {
      rows: [{ key: 'row_1', label: 'Row 1' }],
      columns: [{ key: 'value', label: 'Value', cell: 'text' }],
    };
  }
  if (type === 'paragraph') field.text = 'Some explanatory text.';
  if (type === 'rich_text') {
    field.text =
      '<p>Add your instructions here. You can use <strong>bold</strong>, lists and ' +
      '<a href="https://www.gov.uk/view-driving-licence">links</a> (links open in a new tab).</p>';
  }
  if (type === 'file_upload') {
    field.accept = ['pdf', 'jpg', 'png'];
    field.multiple = false;
  }
  return field;
}

export const DEFAULT_CONSENT: FormConsent = {
  required: true,
  statement:
    'I consent to Corrigenda processing the special-category information I have provided here (such as ethnicity, religion, health, sexual orientation and gender) for the purpose of my onboarding. It is treated in the strictest confidence and stored encrypted.',
};
